import React, { useContext, useEffect } from 'react';
import { Card } from 'styles';
import { Text, Button, Icon, Notification } from '@perch-labs/shrike';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useSessionContext } from 'contexts/session';
import { LoadingModule } from 'components/loading';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { copyFor } from 'config';
import { trackEvent } from 'domain/tracking';
import {
  BodyContainer,
  HeaderContainer,
  TextWrapper,
  UpdatedContent,
  InfoContainer,
} from './user-rewards.style';
import { TextSection } from './sub-section/text-section.component';
import {
  LOOKUP_USER_REWARD,
  GET_REWARDS_REDIRECT_TOKEN,
} from './user-rewards.api';
import { daysCalculator } from './user-rewards.utils';

const getRewardsWidgetCopy = copyFor('solar.rewardsWidget');
const rewardsPendingStatus = ['pending', 'activation_failed'];
const keys = ['nextRewardAmount', 'nextRewardDate', 'totalRewardValue'];

const showPendingMessage = (data, status) => {
  if (rewardsPendingStatus.includes(status)) {
    return true;
  } else if (!data.lookupUserReward?.result) {
    if (keys.some(key => data.lookupUserReward && !data.lookupUserReward[key]))
      return true;
    else return false;
  } else return true;
};

const ButtonComponent = ({ mobile = false, handleRedirection }) => (
  <Button
    color="primaryInverse"
    backgroundColor="primary"
    onClick={handleRedirection}
    size="small"
    data-testid="redirect-button"
  >
    {getRewardsWidgetCopy('redirectToRewardsDashboard')}
    {!mobile && (
      <Icon
        color="primary"
        icon="LinkArrow"
        margin={{ top: '1px', left: '8px' }}
      />
    )}
  </Button>
);

export const UserRewards = ({ mobile = false, userStatus }) => {
  const { addNotification } = useContext(Notification.Context);
  const { userId } = useSessionContext();

  const { data, loading } = useQuery(LOOKUP_USER_REWARD, {
    variables: { userId },
  });

  const [fetchToken, { data: tokenData, error: tokenError }] = useLazyQuery<
    GetRewardsRedirectionTokenQuery,
    GetRewardsRedirectionTokenQueryVariables
  >(GET_REWARDS_REDIRECT_TOKEN, {
    variables: { userId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (tokenData?.getTokenUserReward?.token) {
      trackEvent('track_rewards_dashboard_redirect', {
        metadata: { userId },
      });

      window.open(
        `${import.meta.env.ARCADIA_REWARDS_URL}${
          tokenData?.getTokenUserReward?.token
        }`,
        '_blank'
      );
    }

    if (tokenError)
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: 'Try again later.',
        variant: 'warning',
      });
  }, [tokenError, tokenData, addNotification]);

  const handleRedirection = async () => {
    fetchToken();
  };

  if (loading) return <LoadingModule />;

  return (
    <Card
      margin={{ bottom: '24px' }}
      mobileVariant="flat"
      style={{ backgroundColor: '#004037', color: 'white' }}
    >
      <UpdatedContent>
        <HeaderContainer>
          <TextWrapper>
            <Text textStyle="heading700" tag="h2" color="primaryInverse">
              {getRewardsWidgetCopy('title')}
            </Text>
          </TextWrapper>
          {!showPendingMessage(data, userStatus.getUserReward.status) && (
            <InfoContainer>
              <ButtonComponent
                mobile={mobile}
                handleRedirection={handleRedirection}
              />
            </InfoContainer>
          )}
        </HeaderContainer>
        {!showPendingMessage(data, userStatus.getUserReward.status) && (
          <BodyContainer>
            <TextSection
              header={daysCalculator(data.lookupUserReward?.nextRewardDate)}
              subHeader={getRewardsWidgetCopy('daysToReward')}
            />
            <TextSection
              header={`$${data.lookupUserReward?.nextRewardAmount}`}
              subHeader={getRewardsWidgetCopy('nextReward')}
            />
            <TextSection
              header={`$${data.lookupUserReward?.totalRewardValue}`}
              subHeader={getRewardsWidgetCopy('rewardBalance')}
            />
          </BodyContainer>
        )}
        {showPendingMessage(data, userStatus.getUserReward.status) && (
          <BodyContainer>
            <TextSection
              header={getRewardsWidgetCopy('pendingMessage')}
              active={
                !showPendingMessage(data, userStatus.getUserReward.status)
              }
            />
          </BodyContainer>
        )}
      </UpdatedContent>
    </Card>
  );
};
